import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Clock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NCAA logo (user provided)
const NCAA_LOGO = "https://customer-assets.emergentagent.com/job_asn-gamecenter/artifacts/v36hd9iz_NCAA_logo.svg.png";

// League logo component with text fallback
const LeagueBadge = ({ league }) => {
  const abbr = league?.replace("NCAA", "").substring(0, 3) || league?.substring(0, 3) || "?";
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 bg-zinc-700 text-[8px] font-bold text-white rounded-sm leading-none">
      {abbr}
    </span>
  );
};

// League logos with fallback - ESPN CDN may be blocked on some networks
const LEAGUE_LOGOS = {
  "NFL": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/nfl.png&w=100&h=100&transparent=true",
  "NBA": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/nba.png&w=100&h=100&transparent=true",
  "NHL": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/nhl.png&w=100&h=100&transparent=true",
  "MLB": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/mlb.png&w=100&h=100&transparent=true",
  "WNBA": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/wnba.png&w=100&h=100&transparent=true",
  "MLS": "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/mls.png&w=100&h=100&transparent=true",
  "NCAAB": NCAA_LOGO,
  "NCAAF": NCAA_LOGO,
  "CFB": NCAA_LOGO,
  "NCAAM": NCAA_LOGO,
  "College": NCAA_LOGO,
  "HS-Football": null,
  "HS-Basketball": null,
};

// Official team abbreviations mapping (team name -> official abbreviation)
const TEAM_ABBREVIATIONS = {
  // NBA Teams
  "Los Angeles Lakers": "LAL", "Lakers": "LAL",
  "Toronto Raptors": "TOR", "Raptors": "TOR",
  "Sacramento Kings": "SAC", "Kings": "SAC",
  "Portland Trail Blazers": "POR", "Trail Blazers": "POR", "Blazers": "POR",
  "Memphis Grizzlies": "MEM", "Grizzlies": "MEM",
  "Cleveland Cavaliers": "CLE", "Cavaliers": "CLE", "Cavs": "CLE",
  "Los Angeles Clippers": "LAC", "Clippers": "LAC",
  "Brooklyn Nets": "BKN", "Nets": "BKN",
  "Detroit Pistons": "DET", "Pistons": "DET",
  "Golden State Warriors": "GSW", "Warriors": "GSW",
  "Phoenix Suns": "PHX", "Suns": "PHX",
  "Dallas Mavericks": "DAL", "Mavericks": "DAL", "Mavs": "DAL",
  "Denver Nuggets": "DEN", "Nuggets": "DEN",
  "Boston Celtics": "BOS", "Celtics": "BOS",
  "Miami Heat": "MIA", "Heat": "MIA",
  "Milwaukee Bucks": "MIL", "Bucks": "MIL",
  "Chicago Bulls": "CHI", "Bulls": "CHI",
  "New York Knicks": "NYK", "Knicks": "NYK",
  "Philadelphia 76ers": "PHI", "76ers": "PHI", "Sixers": "PHI",
  "Atlanta Hawks": "ATL", "Hawks": "ATL",
  "Houston Rockets": "HOU", "Rockets": "HOU",
  "Indiana Pacers": "IND", "Pacers": "IND",
  "Minnesota Timberwolves": "MIN", "Timberwolves": "MIN", "Wolves": "MIN",
  "New Orleans Pelicans": "NOP", "Pelicans": "NOP",
  "Oklahoma City Thunder": "OKC", "Thunder": "OKC",
  "Orlando Magic": "ORL", "Magic": "ORL",
  "San Antonio Spurs": "SAS", "Spurs": "SAS",
  "Utah Jazz": "UTA", "Jazz": "UTA",
  "Washington Wizards": "WAS", "Wizards": "WAS",
  "Charlotte Hornets": "CHA", "Hornets": "CHA",
  
  // NHL Teams
  "Boston Bruins": "BOS", "Bruins": "BOS",
  "Buffalo Sabres": "BUF", "Sabres": "BUF",
  "Calgary Flames": "CGY", "Flames": "CGY",
  "Carolina Hurricanes": "CAR", "Hurricanes": "CAR", "Canes": "CAR",
  "Chicago Blackhawks": "CHI", "Blackhawks": "CHI",
  "Colorado Avalanche": "COL", "Avalanche": "COL", "Avs": "COL",
  "Columbus Blue Jackets": "CBJ", "Blue Jackets": "CBJ",
  "Dallas Stars": "DAL", "Stars": "DAL",
  "Detroit Red Wings": "DET", "Red Wings": "DET",
  "Edmonton Oilers": "EDM", "Oilers": "EDM",
  "Florida Panthers": "FLA", "Panthers": "FLA",
  "Los Angeles Kings": "LAK",
  "Minnesota Wild": "MIN", "Wild": "MIN",
  "Montreal Canadiens": "MTL", "Canadiens": "MTL", "Habs": "MTL",
  "Nashville Predators": "NSH", "Predators": "NSH", "Preds": "NSH",
  "New Jersey Devils": "NJD", "Devils": "NJD",
  "New York Islanders": "NYI", "Islanders": "NYI",
  "New York Rangers": "NYR", "Rangers": "NYR",
  "Ottawa Senators": "OTT", "Senators": "OTT", "Sens": "OTT",
  "Philadelphia Flyers": "PHI", "Flyers": "PHI",
  "Pittsburgh Penguins": "PIT", "Penguins": "PIT", "Pens": "PIT",
  "San Jose Sharks": "SJS", "Sharks": "SJS",
  "Seattle Kraken": "SEA", "Kraken": "SEA",
  "St. Louis Blues": "STL", "Blues": "STL",
  "Tampa Bay Lightning": "TBL", "Lightning": "TBL", "Bolts": "TBL",
  "Toronto Maple Leafs": "TOR", "Maple Leafs": "TOR", "Leafs": "TOR",
  "Vancouver Canucks": "VAN", "Canucks": "VAN",
  "Vegas Golden Knights": "VGK", "Golden Knights": "VGK",
  "Washington Capitals": "WSH", "Capitals": "WSH", "Caps": "WSH",
  "Winnipeg Jets": "WPG", "Jets": "WPG",
  
  // College Teams (common)
  "Tyler Junior College": "TJC", "Tyler": "TJC",
  "Kilgore College": "KC", "Kilgore": "KC",
  "Paris Junior College": "PJC", "Paris": "PJC",
  "Jacksonville College": "JAX", "Jacksonville": "JAX",
  "Blinn College": "BLINN", "Blinn": "BLINN",
  "Michigan": "MICH", "Michigan Wolverines": "MICH",
  "Ohio State": "OSU", "Ohio State Buckeyes": "OSU",
  "Texas": "TEX", "Texas Longhorns": "TEX",
  "Alabama": "BAMA", "Alabama Crimson Tide": "BAMA",
  "Georgia": "UGA", "Georgia Bulldogs": "UGA",
  "Dayton": "DAY", "Dayton Flyers": "DAY",
  "Duke": "DUKE", "Duke Blue Devils": "DUKE",
  "Kentucky": "UK", "Kentucky Wildcats": "UK",
  "Kansas": "KU", "Kansas Jayhawks": "KU",
  "North Carolina": "UNC", "Tar Heels": "UNC",
  "UCLA": "UCLA", "UCLA Bruins": "UCLA",
  "USC": "USC", "USC Trojans": "USC",
  "TCU": "TCU", "TCU Horned Frogs": "TCU",
};

// Sport display config
const SPORT_DISPLAY = {
  "NFL": { abbr: "NFL", logo: LEAGUE_LOGOS.NFL },
  "NBA": { abbr: "NBA", logo: LEAGUE_LOGOS.NBA },
  "NHL": { abbr: "NHL", logo: LEAGUE_LOGOS.NHL },
  "MLB": { abbr: "MLB", logo: LEAGUE_LOGOS.MLB },
  "WNBA": { abbr: "WNBA", logo: LEAGUE_LOGOS.WNBA },
  "MLS": { abbr: "MLS", logo: LEAGUE_LOGOS.MLS },
  "CFB": { abbr: "NCAAF", logo: NCAA_LOGO },
  "NCAAM": { abbr: "NCAAB", logo: NCAA_LOGO },
  "NCAAB": { abbr: "NCAAB", logo: NCAA_LOGO },
  "NCAAF": { abbr: "NCAAF", logo: NCAA_LOGO },
  "College": { abbr: "NCAA", logo: NCAA_LOGO },
  "Basketball": { abbr: "NCAAB", logo: NCAA_LOGO },
  "Football": { abbr: "NCAAF", logo: NCAA_LOGO },
  "HS-Football": { abbr: "HSFB", icon: "🏈" },
  "HS-Basketball": { abbr: "HSBB", icon: "🏀" },
};

const TICKER_CATEGORIES = [
  { id: "all", label: "All Sports", icon: "🏆" },
  { id: "divider-1", type: "divider", label: "Pro Leagues" },
  { id: "NFL", label: "NFL", logo: LEAGUE_LOGOS.NFL },
  { id: "NBA", label: "NBA", logo: LEAGUE_LOGOS.NBA },
  { id: "NHL", label: "NHL", logo: LEAGUE_LOGOS.NHL },
  { id: "MLB", label: "MLB", logo: LEAGUE_LOGOS.MLB },
  { id: "WNBA", label: "WNBA", logo: LEAGUE_LOGOS.WNBA },
  { id: "MLS", label: "MLS", logo: LEAGUE_LOGOS.MLS },
  { id: "divider-2", type: "divider", label: "College" },
  { id: "CFB", label: "Football", logo: NCAA_LOGO },
  { id: "NCAAM", label: "Basketball", logo: NCAA_LOGO },
];

// ============================================
// LAYER 1: EVENT COUNTDOWN BAR (Scrolls away)
// ============================================
const EventCountdownBar = () => {
  const [nextBroadcast, setNextBroadcast] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const fetchNext = async () => {
      try {
        const [scheduleRes, liveRes] = await Promise.all([
          axios.get(`${API}/broadcasts/schedule`),
          axios.get(`${API}/broadcasts/live`)
        ]);
        
        if (liveRes.data?.length > 0) {
          setNextBroadcast(liveRes.data[0]);
          setIsLive(true);
          return;
        }
        
        setIsLive(false);
        
        const now = new Date();
        const upcoming = (scheduleRes.data || []).find(b => {
          const date = new Date(`${b.scheduled_date}T${b.scheduled_time}:00`);
          return date > now;
        });
        
        if (upcoming) setNextBroadcast(upcoming);
      } catch (err) {
        console.debug("Error:", err);
      }
    };

    fetchNext();
    const interval = setInterval(fetchNext, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!nextBroadcast || isLive) return;

    const targetDate = `${nextBroadcast.scheduled_date}T${nextBroadcast.scheduled_time}:00`;
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const diff = target - now;

      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          mins: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          secs: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextBroadcast, isLive]);

  if (!nextBroadcast) {
    return (
      <div className="bg-zinc-900 h-10 flex items-center justify-center border-b border-zinc-800">
        <span className="text-zinc-500 text-xs uppercase tracking-wider">No upcoming events</span>
      </div>
    );
  }

  if (isLive) {
    return (
      <Link to={`/game/${nextBroadcast.id}`} className="block bg-orange-500 h-10 hover:bg-orange-400 transition-colors overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-2 md:px-4 h-full flex items-center justify-center gap-2 md:gap-4">
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <span className="w-2 h-2 bg-black rounded-full animate-pulse" />
            <span className="text-black font-bold text-[10px] md:text-xs uppercase tracking-wider">Live:</span>
          </div>
          <span className="text-black font-medium text-xs md:text-sm truncate">{nextBroadcast.title}</span>
          <span className="text-black/70 text-[10px] md:text-xs uppercase font-bold flex-shrink-0">Watch →</span>
        </div>
      </Link>
    );
  }

  const formatDate = () => {
    try {
      const date = new Date(`${nextBroadcast.scheduled_date}T${nextBroadcast.scheduled_time}:00`);
      return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="bg-zinc-900 h-10 flex items-center border-b border-zinc-800 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-2 md:px-4 w-full flex items-center justify-center gap-2 md:gap-6">
        <div className="hidden sm:flex items-center gap-2 md:gap-3 min-w-0 flex-shrink">
          <span className="text-zinc-500 text-[10px] md:text-xs uppercase tracking-wider whitespace-nowrap">Next:</span>
          <span className="text-white text-xs md:text-sm font-medium truncate max-w-[120px] md:max-w-none">{nextBroadcast.title}</span>
          <span className="hidden md:inline text-zinc-600 text-xs">•</span>
          <span className="hidden md:inline text-zinc-400 text-xs whitespace-nowrap">{formatDate()}</span>
        </div>
        
        <div className="sm:hidden flex items-center gap-2 min-w-0">
          <span className="text-zinc-500 text-[10px] uppercase">Next:</span>
          <span className="text-white text-[11px] font-medium truncate max-w-[100px]">{nextBroadcast.title}</span>
        </div>

        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <Clock className="w-3 h-3 text-orange-500 hidden md:block" />
          <div className="flex items-center gap-0.5 md:gap-1 text-[10px] md:text-xs font-mono">
            <span className="bg-zinc-800 px-1 md:px-1.5 py-0.5 text-white font-bold">{String(timeLeft.days).padStart(2, '0')}</span>
            <span className="text-zinc-600">:</span>
            <span className="bg-zinc-800 px-1 md:px-1.5 py-0.5 text-white font-bold">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="text-zinc-600">:</span>
            <span className="bg-zinc-800 px-1 md:px-1.5 py-0.5 text-white font-bold">{String(timeLeft.mins).padStart(2, '0')}</span>
            <span className="text-zinc-600">:</span>
            <span className="bg-zinc-800 px-1 md:px-1.5 py-0.5 text-orange-500 font-bold">{String(timeLeft.secs).padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ESPN-STYLE GAME CARD (Compact)
// ============================================
const GameCard = ({ score, isFirst }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "live": return "text-red-500";
      case "final": return "text-zinc-500";
      default: return "text-zinc-400";
    }
  };

  const getStatusText = (score) => {
    if (score.status === "live") {
      return score.period ? `${score.period}`.trim() : "LIVE";
    }
    if (score.status === "final") return "FINAL";
    if (score.game_time) {
      const date = new Date(score.game_time);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      return `${month}/${day} ${time}`;
    }
    return "";
  };

  // Get official team abbreviation
  const getAbbr = (team, espnAbbr) => {
    // Use ESPN-provided abbreviation first (most reliable)
    if (espnAbbr) return espnAbbr;
    
    const teamName = typeof team === 'object' ? (team.name || team.abbreviation) : team;
    if (!teamName) return "TBD";
    
    // Check official abbreviations lookup
    if (TEAM_ABBREVIATIONS[teamName]) {
      return TEAM_ABBREVIATIONS[teamName];
    }
    
    // Check if team object has abbreviation
    if (typeof team === 'object' && team.abbreviation) {
      return team.abbreviation.substring(0, 4).toUpperCase();
    }
    
    // Generate abbreviation from team name (first letters of each word)
    const words = teamName.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return words.map(w => w[0]).join('').substring(0, 4).toUpperCase();
    }
    return teamName.substring(0, 4).toUpperCase();
  };

  // Get team record if available
  const getRecord = (team) => {
    if (typeof team === 'object' && team.record) {
      return team.record;
    }
    return null;
  };

  const awayAbbr = getAbbr(score.away_team, score.away_abbr);
  const homeAbbr = getAbbr(score.home_team, score.home_abbr);
  const awayRecord = getRecord(score.away_team);
  const homeRecord = getRecord(score.home_team);
  const awayLogo = score.away_logo || score.away_team_logo || score.away_team?.logo;
  const homeLogo = score.home_logo || score.home_team_logo || score.home_team?.logo;

  // Determine if game has started (has scores)
  const gameStarted = score.status === "live" || score.status === "final" || 
    (score.away_score !== null && score.away_score !== undefined && score.away_score !== 0) ||
    (score.home_score !== null && score.home_score !== undefined && score.home_score !== 0);

  // Determine winner for final games
  const awayWon = score.status === "final" && score.away_score > score.home_score;
  const homeWon = score.status === "final" && score.home_score > score.away_score;

  return (
    <div className={`flex-shrink-0 w-[115px] bg-zinc-900 h-full ${!isFirst ? 'border-l border-zinc-700' : ''}`}>
      {/* Status/Time Header */}
      <div className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-center border-b border-zinc-800 ${getStatusColor(score.status)}`}>
        {score.status === "live" && <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1 animate-pulse" />}
        {getStatusText(score)}
      </div>
      
      {/* Teams - Compact */}
      <div className="px-1.5 py-1 space-y-0.5">
        {/* Away Team */}
        <div className={`flex items-center justify-between ${awayWon ? '' : score.status === 'final' ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {awayLogo ? (
              <img src={awayLogo} alt="" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
            ) : (
              <div className="w-3.5 h-3.5 bg-zinc-700 rounded flex-shrink-0" />
            )}
            <span className={`text-[10px] font-bold truncate ${awayWon ? 'text-white' : 'text-zinc-300'}`}>{awayAbbr}</span>
          </div>
          {/* Show score if game started, otherwise show record */}
          {gameStarted ? (
            <span className={`text-xs font-bold tabular-nums ml-1 ${awayWon ? 'text-white' : 'text-zinc-400'}`}>
              {score.away_score ?? 0}
            </span>
          ) : awayRecord ? (
            <span className="text-[9px] text-zinc-500 ml-1">{awayRecord}</span>
          ) : (
            <span className="text-[9px] text-zinc-600 ml-1">-</span>
          )}
        </div>
        
        {/* Home Team */}
        <div className={`flex items-center justify-between ${homeWon ? '' : score.status === 'final' ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {homeLogo ? (
              <img src={homeLogo} alt="" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
            ) : (
              <div className="w-3.5 h-3.5 bg-zinc-700 rounded flex-shrink-0" />
            )}
            <span className={`text-[10px] font-bold truncate ${homeWon ? 'text-white' : 'text-zinc-300'}`}>{homeAbbr}</span>
          </div>
          {/* Show score if game started, otherwise show record */}
          {gameStarted ? (
            <span className={`text-xs font-bold tabular-nums ml-1 ${homeWon ? 'text-white' : 'text-zinc-400'}`}>
              {score.home_score ?? 0}
            </span>
          ) : homeRecord ? (
            <span className="text-[9px] text-zinc-500 ml-1">{homeRecord}</span>
          ) : (
            <span className="text-[9px] text-zinc-600 ml-1">-</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SPORT HEADER BLOCK (Compact)
// ============================================
const SportHeader = ({ league }) => {
  const display = SPORT_DISPLAY[league] || { abbr: league, logo: NCAA_LOGO };
  
  return (
    <div className="flex-shrink-0 w-[50px] bg-zinc-800 flex flex-col items-center justify-center h-full border-l border-zinc-700 first:border-l-0">
      {display.logo ? (
        <img src={display.logo} alt={display.abbr} className="w-5 h-5 object-contain mb-0.5" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
      ) : null}
      <span className="text-[8px] font-bold text-white uppercase tracking-wide">{display.abbr}</span>
    </div>
  );
};

// ============================================
// LAYER 2: ESPN-STYLE STATIC SCORE TICKER (Compact)
// ============================================
const ESPNStyleTicker = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showDropdown, setShowDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  
  const scrollContainerRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownPortalRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fetchScores = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      const category = TICKER_CATEGORIES.find(c => c.id === selectedCategory);
      if (selectedCategory !== "all" && category) {
        params.append("league", selectedCategory);
      }
      const url = `${API}/scores${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await axios.get(url);
      setScores(response.data);
      setScrollPosition(0);
    } catch (error) {
      console.error("Error fetching scores:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchScores();
    const interval = setInterval(() => fetchScores(), 30000);
    return () => clearInterval(interval);
  }, [fetchScores]);

  // Group scores by league when "all" is selected
  const groupedScores = useCallback(() => {
    if (selectedCategory !== "all") {
      return [{ league: null, games: scores }];
    }
    
    const groups = {};
    scores.forEach(score => {
      const league = score.league || score.sport || "Other";
      if (!groups[league]) {
        groups[league] = [];
      }
      groups[league].push(score);
    });
    
    return Object.entries(groups).map(([league, games]) => ({ league, games }));
  }, [scores, selectedCategory]);

  const getCurrentCategoryLabel = () => {
    const cat = TICKER_CATEGORIES.find(c => c.id === selectedCategory);
    return cat?.label || "All Sports";
  };

  const getCurrentCategoryLogo = () => {
    const cat = TICKER_CATEGORIES.find(c => c.id === selectedCategory);
    return cat?.logo || null;
  };

  // Scroll handlers
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const newPos = Math.max(0, scrollPosition - 360);
      scrollContainerRef.current.scrollTo({ left: newPos, behavior: 'smooth' });
      setScrollPosition(newPos);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth;
      const newPos = Math.min(maxScroll, scrollPosition + 360);
      scrollContainerRef.current.scrollTo({ left: newPos, behavior: 'smooth' });
      setScrollPosition(newPos);
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
    }
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollContainerRef.current 
    ? scrollPosition < scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth - 10
    : false;

  useEffect(() => {
    if (showDropdown && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom, left: rect.left });
    }
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e) => {
      const isInsideButton = dropdownButtonRef.current && dropdownButtonRef.current.contains(e.target);
      const isInsidePortal = dropdownPortalRef.current && dropdownPortalRef.current.contains(e.target);
      if (!isInsideButton && !isInsidePortal) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const DropdownPortal = () => {
    if (!showDropdown) return null;
    
    return createPortal(
      <div 
        ref={dropdownPortalRef}
        className="fixed w-44 bg-zinc-900 border border-zinc-700 shadow-2xl max-h-[60vh] overflow-y-auto"
        style={{ top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 99999 }}
        data-testid="ticker-dropdown-portal"
      >
        {TICKER_CATEGORIES.map((item) => {
          if (item.type === "divider") {
            return (
              <div key={item.id} className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-zinc-500 bg-zinc-800/50">
                {item.label}
              </div>
            );
          }
          return (
            <button
              key={item.id}
              onClick={() => { setSelectedCategory(item.id); setShowDropdown(false); }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left ${selectedCategory === item.id ? 'bg-orange-500 text-black' : 'text-zinc-300 hover:bg-zinc-800'}`}
            >
              {item.logo ? <img src={item.logo} alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.replaceWith(Object.assign(document.createElement('span'), { className: 'w-4 text-center text-[8px] font-bold text-zinc-400', textContent: item.label?.substring(0,3) })); }} /> : <span className="w-4 text-center text-xs">{item.icon || item.label?.substring(0,3)}</span>}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>,
      document.body
    );
  };

  const grouped = groupedScores();
  const totalGames = scores.length;

  return (
    <div className="bg-black h-[62px] flex items-stretch border-b border-zinc-800 relative overflow-hidden" data-testid="score-ticker">
      {/* Left: Category Selector */}
      <div ref={dropdownButtonRef} className="relative flex-shrink-0 border-r border-zinc-700">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex flex-col items-center justify-center gap-0.5 px-3 h-full text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-900/50 transition-colors min-w-[70px]"
          data-testid="ticker-category-btn"
        >
          {getCurrentCategoryLogo() ? (
            <img src={getCurrentCategoryLogo()} alt="" className="w-4 h-4 object-contain" />
          ) : (
            <span className="text-sm">🏆</span>
          )}
          <div className="flex items-center gap-0.5">
            <span className="text-[8px] max-w-[50px] truncate">{getCurrentCategoryLabel()}</span>
            <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </div>
        </button>
        <DropdownPortal />
      </div>

      {/* Left Arrow */}
      <button
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        className={`flex-shrink-0 w-6 flex items-center justify-center border-r border-zinc-700 transition-colors ${
          canScrollLeft ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'text-zinc-700 cursor-not-allowed'
        }`}
        data-testid="ticker-scroll-left"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Scores Container */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {loading ? (
          <div className="flex items-center h-full gap-2 px-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="w-[120px] h-[44px] bg-zinc-800 rounded animate-pulse flex-shrink-0" />
            ))}
          </div>
        ) : totalGames > 0 ? (
          <div className="flex h-full">
            {grouped.map((group, groupIdx) => (
              <div key={group.league || groupIdx} className="flex h-full">
                {selectedCategory === "all" && group.league && (
                  <SportHeader league={group.league} />
                )}
                {group.games.map((score, idx) => (
                  <GameCard 
                    key={`${score.id}-${idx}`} 
                    score={score} 
                    isFirst={idx === 0 && (!group.league || selectedCategory !== "all")}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full px-4">
            <span className="text-zinc-600 text-xs">No scores available</span>
          </div>
        )}
      </div>

      {/* Right Arrow */}
      <button
        onClick={scrollRight}
        disabled={!canScrollRight}
        className={`flex-shrink-0 w-6 flex items-center justify-center border-l border-zinc-700 transition-colors ${
          canScrollRight ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'text-zinc-700 cursor-not-allowed'
        }`}
        data-testid="ticker-scroll-right"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Refresh Button */}
      <button
        onClick={() => fetchScores(true)}
        disabled={refreshing}
        className="flex-shrink-0 w-8 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-zinc-800 border-l border-zinc-700 transition-colors"
        data-testid="ticker-refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

// ============================================
// COMBINED: ESPN-Style Score Ticker Only
// ============================================
export const ScoreTicker = () => {
  return (
    <div 
      className="bg-black safe-area-top safe-area-x"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* ESPN-Style Score Ticker */}
      <ESPNStyleTicker />
    </div>
  );
};

export default ScoreTicker;
